import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-async-hook';

import { investorApiClient } from 'api';
import { CurrencyDto } from 'types';

import { CurrencySelectionContext } from './context';

interface Props {
  loadCurrencies?: () => Promise<CurrencyDto[]>;
}

const loadInvestorCurrencies = async () => {
  const { data } = await investorApiClient.investors.investorApiControllerGetCurrencies();
  return data;
};

export const CurrencySelectionProvider = ({
  children,
  loadCurrencies = loadInvestorCurrencies,
}: PropsWithChildren<Props>) => {
  const [selectedCurrency, setSelectedCurrency] = useState('');

  const {
    result: currencies = [],
    loading: currenciesLoading,
    error: currenciesError,
  } = useAsync(async () => {
    return loadCurrencies();
  }, [loadCurrencies]);

  const currencyOptions = useMemo(
    () =>
      currencies.map((currency) => ({
        value: currency.isoCode,
        label: currency.isoCode,
      })),
    [currencies],
  );

  useEffect(() => {
    if (!currencies.length) {
      if (!currenciesLoading) {
        console.error('No currencies available for the global currency switch.');
      }
      return;
    }

    if (!selectedCurrency) {
      setSelectedCurrency(currencies[0].isoCode);
      return;
    }

    if (!currencies.find((currency) => currency.isoCode === selectedCurrency)) {
      setSelectedCurrency(currencies[0].isoCode);
    }
  }, [currencies, currenciesLoading, selectedCurrency]);

  useEffect(() => {
    if (currenciesError) {
      console.error('Failed to load currencies for the global currency switch.', currenciesError);
    }
  }, [currenciesError]);

  const handleSetSelectedCurrency = useCallback((currency: string) => {
    setSelectedCurrency(currency);
  }, []);

  return (
    <CurrencySelectionContext.Provider
      value={{
        currencies,
        currencyOptions,
        currenciesLoading,
        currenciesError: currenciesError ?? null,
        selectedCurrency,
        setSelectedCurrency: handleSetSelectedCurrency,
      }}
    >
      {children}
    </CurrencySelectionContext.Provider>
  );
};
